import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
    TextField, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, IconButton, Select, MenuItem, InputLabel, FormControl,
    Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, MoneyOff as RefundIcon } from '@mui/icons-material';
import SimpleDatePicker from '../../../../ui/simpleDatePicker';
import { formatAmountForDisplay, formatDateWithTimezone } from '../../../../../utils';

// Status chip component
function RefundStatusChip({ status }) {
    const getStatusColor = (status) => {
        switch (status) {
            case 'pending': return 'warning';
            case 'approved': return 'info';
            case 'processed': return 'success';
            case 'cancelled': return 'error';
            default: return 'default';
        }
    };

    // Handle undefined or null status
    const displayStatus = status || 'pending';

    return (
        <Chip 
            label={displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)} 
            color={getStatusColor(displayStatus)} 
            size="small" 
        />
    );
}

// Helper component for the Add/Edit Refund Dialog
function AddEditRefundDialog({ open, onClose, refundData, onSave, payments = [], maxRefundAmount = 0 }) {
    const [refundMethod, setRefundMethod] = useState('cash');
    const [refundAmount, setRefundAmount] = useState('');
    const [reason, setReason] = useState('');
    const [authorizedBy, setAuthorizedBy] = useState('');
    const [notes, setNotes] = useState('');
    const [paymentId, setPaymentId] = useState('');
    const [refundDate, setRefundDate] = useState('');
    const [errors, setErrors] = useState({});

    const refundMethods = [
        { value: 'cash', label: 'Cash' },
        { value: 'credit', label: 'Credit Card' },
        { value: 'debit', label: 'Debit Card' },
        { value: 'check', label: 'Check' },
        { value: 'bank_transfer', label: 'Bank Transfer' },
        { value: 'other', label: 'Other' }
    ];

    // Populate form when refundData changes (for editing)
    useEffect(() => {
        if (open && refundData) {
            setRefundAmount(refundData.refundAmount || '');
            setReason(refundData.reason || '');
            setRefundMethod(refundData.refundMethod || 'cash');
            setAuthorizedBy(refundData.authorizedBy || '');
            setNotes(refundData.notes || '');
            setPaymentId(refundData.paymentId || '');
            setRefundDate(refundData.refundDate ? formatDateWithTimezone(refundData.refundDate) : new Date());
        } else if (open) {
            // Reset form for new refund
            setRefundAmount('');
            setReason('');
            setRefundMethod('cash');
            setAuthorizedBy('');
            setNotes('');
            setPaymentId('');
            setRefundDate('');
        }
        setErrors({}); // Clear errors on open/data change
    }, [open, refundData]);

    // Validate form data
    const validate = () => {
        const newErrors = {};

        if (!refundAmount || isNaN(refundAmount) || parseFloat(refundAmount) <= 0) {
            newErrors.refundAmount = 'Valid refund amount is required';
        } else if (parseFloat(refundAmount) > maxRefundAmount) {
            newErrors.refundAmount = `Refund amount cannot exceed $${maxRefundAmount.toFixed(2)}`;
        }

        if (!reason || reason.trim().length < 3) {
            newErrors.reason = 'Reason is required (minimum 3 characters)';
        }

        if (!refundDate) {
            newErrors.refundDate = 'Refund date is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = () => {
        if (!validate()) return;

        const refundDataToSave = {
            refundAmount: parseFloat(refundAmount),
            reason: reason.trim(),
            refundMethod,
            authorizedBy: authorizedBy.trim() || null,
            notes: notes.trim() || null,
            paymentId: paymentId || null,
            refundDate: refundDate, // Already in YYYY-MM-DD format
            status: refundData?.status || 'approved' // Ensure status is preserved or set to default
        };

        if (refundData) {
            // Editing existing refund
            refundDataToSave.id = refundData.id;
        }

        onSave(refundDataToSave);
        onClose();
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {refundData ? 'Edit Refund' : 'Process Refund'}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    {/* Refund Amount */}
                    <TextField
                        label="Refund Amount"
                        type="number"
                        value={refundAmount}
                        onChange={(e) => setRefundAmount(e.target.value)}
                        error={!!errors.refundAmount}
                        helperText={errors.refundAmount || `Maximum refundable: $${maxRefundAmount.toFixed(2)}`}
                        fullWidth
                        required
                        inputProps={{ step: '0.01', min: '0' }}
                    />

                    {/* Reason */}
                    <TextField
                        label="Reason for Refund"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        error={!!errors.reason}
                        helperText={errors.reason}
                        fullWidth
                        required
                        multiline
                        rows={3}
                        placeholder="Please provide a detailed reason for the refund..."
                    />

                    {/* Refund Method */}
                    <FormControl fullWidth>
                        <InputLabel>Refund Method</InputLabel>
                        <Select
                            value={refundMethod}
                            onChange={(e) => setRefundMethod(e.target.value)}
                            label="Refund Method"
                        >
                            {refundMethods.map((method, index) => (
                                <MenuItem key={`refund-method-${method.value}-${index}`} value={method.value}>
                                    {method.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Related Payment (Optional) */}
                    {payments.length > 0 && (
                        <FormControl fullWidth>
                            <InputLabel>Related Payment (Optional)</InputLabel>
                            <Select
                                value={paymentId}
                                onChange={(e) => setPaymentId(e.target.value)}
                                label="Related Payment (Optional)"
                                displayEmpty
                            >
                                <MenuItem key="no-payment" value="">None</MenuItem>
                                {payments.map((payment, index) => (
                                    <MenuItem key={`payment-${payment.id || index}`} value={payment.id}>
                                        {formatDateWithTimezone(payment.paymentDate)} - ${payment.paymentAmount}
                                        {payment.paymentMethod && ` (${payment.paymentMethod.name})`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    {/* Refund Date */}
                    <SimpleDatePicker
                        label="Refund Date"
                        value={refundDate}
                        onChange={setRefundDate}
                        error={!!errors.refundDate}
                        helperText={errors.refundDate}
                        fullWidth
                        required
                    />

                    {/* Authorized By */}
                    <TextField
                        label="Authorized By"
                        value={authorizedBy}
                        onChange={(e) => setAuthorizedBy(e.target.value)}
                        fullWidth
                        placeholder="Manager name or authorization code"
                    />

                    {/* Notes */}
                    <TextField
                        label="Additional Notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Any additional notes about this refund..."
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button onClick={handleSave} variant="contained" color="primary">
                    {refundData ? 'Update Refund' : 'Process Refund'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// Main RefundCollector component
const RefundCollector = ({ 
    refunds = [], 
    onAddRefund, 
    onEditRefund, 
    onDeleteRefund, 
    onUpdateRefundStatus,
    disabled = false,
    payments = [],
    maxRefundAmount = 0
}) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingRefund, setEditingRefund] = useState(null);

    
    const totalRefunded = useMemo(() => {
        return refunds
            .filter(refund => refund.status === 'approved' || refund.status === 'processed')
            .reduce((sum, refund) => sum + parseFloat(refund.refundAmount), 0);
    }, [refunds]);

    const handleAddClick = () => {
        setEditingRefund(null);
        setDialogOpen(true);
    };

    const handleEditClick = (refund) => {
        setEditingRefund(refund);
        setDialogOpen(true);
    };

    const handleSave = (refundData) => {
        if (editingRefund) {
            onEditRefund(refundData);
        } else {
            onAddRefund(refundData);
        }
    };

    const handleStatusUpdate = (refundId, newStatus) => {
        onUpdateRefundStatus(refundId, newStatus);
    };

    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RefundIcon />
                    Refunds
                    {totalRefunded > 0 && (
                        <Chip 
                            label={`Total: ${formatAmountForDisplay(totalRefunded)}`} 
                            color="error" 
                            size="small" 
                        />
                    )}
                </Typography>
                <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={disabled || maxRefundAmount <= 0}
                >
                    Process Refund
                </Button>
            </Box>

            {refunds.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                    No refunds processed
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Amount</TableCell>
                                <TableCell>Method</TableCell>
                                <TableCell>Reason</TableCell>
                                {/* <TableCell>Status</TableCell> */}
                                <TableCell>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {refunds.map((refund) => (
                                <TableRow 
                                    key={refund.id || refund.tempId}
                                    sx={{
                                        opacity: refund._markedForDeletion ? 0.4 : 1,
                                        textDecoration: refund._markedForDeletion ? 'line-through' : 'none'
                                    }}
                                >
                                    <TableCell>
                                        {refund._markedForDeletion && (
                                            <Typography
                                                component="span"
                                                sx={{
                                                    color: '#fff',
                                                    fontSize: '0.75rem',
                                                    fontWeight: 'bold',
                                                    mr: 1,
                                                    px: 0.5,
                                                    py: 0.25,
                                                    backgroundColor: '#d32f2f',
                                                    borderRadius: 0.5,
                                                    display: 'inline-block',
                                                    opacity: 1
                                                }}
                                            >
                                                MARKED FOR DELETION
                                            </Typography>
                                        )}
                                        {formatDateWithTimezone(refund.refundDate)}
                                    </TableCell>
                                    <TableCell>
                                        <Typography color="error" fontWeight="bold">
                                            -{formatAmountForDisplay(refund.refundAmount)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {refund.refundMethod?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    </TableCell>
                                    <TableCell>
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                maxWidth: 200, 
                                                overflow: 'hidden', 
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                            title={refund.reason}
                                        >
                                            {refund.reason}
                                        </Typography>
                                    </TableCell>
                                    {/* <TableCell>
                                        <RefundStatusChip status={refund.status} />
                                    </TableCell> */}
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            {refund.status === 'pending' && (
                                                <>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleEditClick(refund)}
                                                        disabled={disabled}
                                                        title="Edit refund"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    {/* <IconButton
                                                        size="small"
                                                        onClick={() => handleStatusUpdate(refund.id, 'approved')}
                                                        disabled={disabled}
                                                        color="success"
                                                        title="Approve refund"
                                                    >
                                                        ✓
                                                    </IconButton> */}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => onDeleteRefund(refund.id || refund.tempId)}
                                                        disabled={disabled}
                                                        color="error"
                                                        title="Delete refund"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </>
                                            )}
                                            {/* <IconButton
                                                size="small"
                                                onClick={() => onDeleteRefund(refund.id || refund.tempId)}
                                                disabled={disabled}
                                                color="error"
                                                title="Delete refund"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>   */}
                                            {/* {refund.status === 'approved' && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleStatusUpdate(refund.id, 'processed')}
                                                    disabled={disabled}
                                                    color="primary"
                                                    title="Mark as processed"
                                                >
                                                    ✓✓
                                                </IconButton>
                                            )} */}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <AddEditRefundDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                refundData={editingRefund}
                onSave={handleSave}
                payments={payments}
                maxRefundAmount={maxRefundAmount}
            />
        </Box>
    );
};

export default RefundCollector;
